import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Gamepad2, ChevronLeft, Loader2, Shield, ToggleLeft, ToggleRight, Home
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import adminApi from "../../lib/adminApi";

const ADMIN_EMAIL = "cpt.serdarakdemir@gmail.com";

export default function AdminGames() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [games, setGames] = useState([]);

  useEffect(() => {
    fetchGames();
  }, []);

  const fetchGames = async () => {
    try {
      const response = await adminApi.getGames();
      setGames(response.data);
    } catch (error) {
      console.error("Games error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (gameId, currentEnabled) => {
    try {
      await adminApi.toggleGame(gameId, !currentEnabled);
      toast.success(`Oyun ${!currentEnabled ? 'aktif' : 'pasif'} edildi`);
      fetchGames();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  // Check admin access
  if (user?.email !== ADMIN_EMAIL) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Yetkisiz Erişim</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-games">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/dashboard" className="p-2 rounded-lg bg-[#4DFFFF]/20 hover:bg-[#4DFFFF]/30 text-[#4DFFFF]" title="Ana Sayfa">
          <Home className="w-5 h-5" />
        </Link>
        <Link to="/admin" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white" title="Admin Panel">
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <div className="flex items-center gap-3">
          <Gamepad2 className="w-6 h-6 text-[#F97316]" />
          <h1 className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
            Oyun Yönetimi
          </h1>
        </div>
      </div>

      {/* Games List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#4DFFFF]" />
        </div>
      ) : (
        <div className="space-y-4">
          {games.map((game) => {
            const isEnabled = game.enabled !== false;
            
            return (
              <div key={game.game_id} className="glass-card p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${game.color}20` }}
                    >
                      <Gamepad2 className="w-6 h-6" style={{ color: game.color }} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-white font-medium">{game.name}</h3>
                        {game.is_system && (
                          <span className="px-2 py-0.5 rounded text-xs bg-[#4DFFFF]/20 text-[#4DFFFF]">Sistem</span>
                        )}
                        {!game.is_system && (
                          <span className="px-2 py-0.5 rounded text-xs bg-[#C6A87C]/20 text-[#C6A87C]">Özel</span>
                        )}
                      </div>
                      <p className="text-sm text-[#64748B]">{game.description?.slice(0, 60)}...</p>
                      <p className="text-xs text-[#64748B] mt-1">
                        {game.league_count || 0} lig • {game.min_players}-{game.max_players} oyuncu • {game.scoring_type}
                      </p>
                    </div>
                  </div>
                  
                  {!game.is_system && (
                    <button
                      onClick={() => handleToggle(game.game_id, isEnabled)}
                      className={`p-2 rounded-lg transition-colors ${isEnabled ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}
                    >
                      {isEnabled ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8" />}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
